import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

const PRIMARY = '#00A7E3';
const ACCENT = '#102E5C';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: 8 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  const handleGoogleResponse = useCallback(async (response) => {
    if (response.credential) {
      setLoading(true);
      setError('');
      const result = await googleLogin(response.credential);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.message || 'Google login failed');
      }
      setLoading(false);
    }
  }, [googleLogin, navigate]);

  const googleBtnRef = React.useRef(null);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'PLACEHOLDER_NEEDS_SETUP') return;

    const initGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          use_fedcm_for_prompt: false,
        });
        if (googleBtnRef.current) {
          googleBtnRef.current.innerHTML = '';
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            type: 'standard', theme: 'outline', size: 'large',
            text: 'continue_with', shape: 'rectangular', width: 400,
          });
        }
      }
    };

    if (window.google?.accounts?.id) {
      initGoogle();
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initGoogle;
      document.head.appendChild(script);
    }
  }, [handleGoogleResponse]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setUnverifiedEmail('');
    setResent(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnverifiedEmail('');
    setLoading(true);

    if (rememberMe) {
      localStorage.setItem('rememberedEmail', formData.email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }

    const result = await login(formData.email, formData.password);
    if (result.success) {
      navigate('/dashboard');
    } else if (result.emailNotVerified) {
      setUnverifiedEmail(result.email || formData.email);
      setError('');
    } else {
      setError(result.message || 'Login failed');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const api = (await import('../services/api')).default;
      await api.post('/auth/resend-verification', { email: String(unverifiedEmail).trim().toLowerCase() });
      setResent(true);
    } catch {}
    setResending(false);
  };


  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#f8fafc' }}>
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, sm: 5 },
      }}>
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT, mb: 0.5 }}>
              Welcome back
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sign in to your account
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>{error}</Alert>}

          {unverifiedEmail && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Your email is not verified yet. Enter the 6-digit code from your email, or resend a new code.
              </Typography>
              <Button
                size="small" variant="outlined" color="warning"
                onClick={handleResend}
                disabled={resending || resent}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1, mr: 1, mb: 1 }}
              >
                {resent ? 'Sent!' : resending ? 'Sending…' : 'Resend code'}
              </Button>
              <Button
                size="small" variant="text" color="warning"
                component={RouterLink}
                to={`/verify-email?email=${encodeURIComponent(unverifiedEmail)}`}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1, mb: 1 }}
              >
                Enter code
              </Button>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth label="Email" name="email" type="email"
              value={formData.email} onChange={handleChange}
              required size="small"
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
            <TextField
              fullWidth label="Password" name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password} onChange={handleChange}
              required size="small"
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <FormControlLabel
                control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} size="small" sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }} />}
                label={<Typography variant="body2" sx={{ fontSize: '13px' }}>Remember me</Typography>}
              />
              <Link component={RouterLink} to="/forgot-password" sx={{ color: PRIMARY, textDecoration: 'none', fontSize: '13px', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit" fullWidth variant="contained" disabled={loading}
              sx={{
                py: 1.25, mb: 2.5, bgcolor: PRIMARY, borderRadius: 1,
                fontSize: '14px', fontWeight: 600, textTransform: 'none',
                boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Sign in'}
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
              <Divider sx={{ flex: 1 }} />
              <Typography variant="caption" sx={{ px: 2, color: 'text.disabled' }}>or</Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            <Box sx={{ position: 'relative', mb: 3, height: 40 }}>
              <Button fullWidth variant="outlined" size="small"
                sx={{
                  py: 1, borderRadius: 1, borderColor: 'divider', height: 40,
                  color: 'text.primary', textTransform: 'none', fontWeight: 500, fontSize: '13px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  '&:hover': { borderColor: PRIMARY, bgcolor: `${PRIMARY}08` },
                }}>
                <GoogleIcon /> Continue with Google
              </Button>
              <Box ref={googleBtnRef} sx={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                opacity: 0.01, overflow: 'hidden', zIndex: 2,
                '& > div': { width: '100% !important', height: '100% !important' },
                '& iframe': { width: '100% !important', height: '40px !important', minHeight: '40px !important' },
              }} />
            </Box>

            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '13px' }}>
              Don't have an account?{' '}
              <Link component={RouterLink} to="/register" sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                Sign up
              </Link>
            </Typography>
          </form>
        </Box>
      </Box>

      <BrandPanel
        headline="Your digital identity, simplified"
        sub="Manage contacts, share your card, and track engagement — all in one place."
      />
    </Box>
  );
};

const BrandPanel = ({ headline, sub }) => {
  const proofItems = [
    { value: '100K+', label: 'Card Views' },
    { value: '1,000+', label: 'Cards Created' },
    { value: '500+', label: 'Active Users' },
    { value: '99.9%', label: 'Uptime' },
  ];

  return (
    <Box sx={{
      flex: 1,
      background: `linear-gradient(160deg, ${ACCENT} 0%, ${PRIMARY} 100%)`,
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      p: 6,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <Box sx={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
      <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 280, height: 280, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

      <Box sx={{ textAlign: 'center', color: '#fff', zIndex: 1, maxWidth: 440 }}>
        <a href="https://webcard.digital/" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
          <img src="/logo.png" alt="WebCard Digital" style={{ height: 44, marginBottom: 32, filter: 'brightness(0) invert(1)', cursor: 'pointer' }} />
        </a>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, lineHeight: 1.3 }}>
          {headline}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8, lineHeight: 1.7, mb: 5 }}>
          {sub}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 4 }}>
          {proofItems.map((item) => (
            <Box key={item.label} sx={{
              py: 2, px: 2.5,
              borderRadius: '12px',
              bgcolor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)',
              textAlign: 'center',
            }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                {item.value}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontWeight: 500 }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
          Trusted by professionals worldwide
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
